import { z } from "zod";
import { jobStateSchema } from "./job-state";
import { operationSchema } from "./job-request";

export const documentAdminPermissionSchema = z.enum([
  "document.overview.read",
  "document.jobs.read",
  "document.runners.read",
  "document.jobs.cancel",
  "document.jobs.retry",
  "document.quotas.read",
  "document.quotas.write",
  "document.corpus.write",
]);

export type DocumentAdminPermission = z.infer<typeof documentAdminPermissionSchema>;

export const documentAdminJobQuerySchema = z.object({
  status: jobStateSchema.optional(),
  operation: operationSchema.optional(),
  ownerId: z.string().trim().min(1).max(128).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type DocumentAdminJobQuery = z.infer<typeof documentAdminJobQuerySchema>;

export const documentAdminActionSchema = z.object({
  confirmed: z.literal(true),
  reason: z.string().trim().min(5).max(500),
});

export const documentOperationDailyLimitsSchema = z.object({
  merge: z.number().int().min(0).max(10_000),
  split: z.number().int().min(0).max(10_000),
  compress: z.number().int().min(0).max(10_000),
  pdf_to_docx: z.number().int().min(0).max(10_000),
  pdf_to_images: z.number().int().min(0).max(10_000),
  convert: z.number().int().min(0).max(10_000),
  ocr: z.number().int().min(0).max(10_000),
});

export const documentQuotaPolicySchema = z.object({
  dailyJobLimit: z.number().int().min(1).max(10_000),
  concurrentJobLimit: z.number().int().min(1).max(10),
  operationDailyLimits: documentOperationDailyLimitsSchema,
});

export type DocumentQuotaPolicy = z.infer<typeof documentQuotaPolicySchema>;

export const documentAdminQuotaUpdateSchema = documentQuotaPolicySchema.extend({
  confirmed: z.literal(true),
  reason: z.string().trim().min(5).max(500),
});

export const documentCorpusHandoffRequestSchema = z.object({
  confirmed: z.literal(true),
  reason: z.string().trim().min(5).max(500),
  outputAssetId: z.string().trim().min(1).max(160).optional(),
  languageHints: z.array(z.string().regex(/^[A-Za-z0-9-]{2,35}$/)).max(16).refine(
    (values) => new Set(values).size === values.length,
    "Language hints must be unique.",
  ).default([]),
}).strict();

export type DocumentCorpusHandoffRequest = z.infer<typeof documentCorpusHandoffRequestSchema>;
